import React from "react";
import { Flex, Form, theme } from "antd";
import type { GlobalToken } from "antd";
import styled from "styled-components";
import { useTranslate } from "@refinedev/core";
import { TrashIcon } from "@phosphor-icons/react";
import {
  Button,
  Input,
  Pill,
  Popconfirm,
  RadioButton,
  RadioGroup,
  radius,
} from "@libertify/ui";
import { QUIZ_ANSWERS_PER_QUESTION } from "../types";

const KEY = "experienceEdit.quizCard";

/** Option positions, so the answer rows render from a stable list rather than
 *  a `Form.List` — the count is fixed by the schema and never edited. */
const ANSWER_INDEXES = Array.from(
  { length: QUIZ_ANSWERS_PER_QUESTION },
  (_, index) => index
);

export interface QuizQuestionItemProps {
  /** Index of this question within the `questions` Form.List. */
  name: number;
  /** 1-based position for the "Question n" label — follows the list, so the
   *  questions renumber after a removal. */
  position: number;
  onRemove: () => void;
}

/**
 * One question of the Quiz edit card: its number, a Remove action, the
 * question text, and the four answer options with a radio marking the correct
 * one. Must be rendered inside the card's Form.
 *
 * The options are a fixed four (the backend schema is explicit about it), so
 * they are neither added nor removed here — only their text is edited, and
 * which of them is right.
 *
 * Removal persists with the next autosave, so a question that carries text
 * asks for confirmation first (same rule as Quick recall's cards); a blank,
 * just-added row goes without asking.
 */
export const QuizQuestionItem: React.FC<QuizQuestionItemProps> = ({
  name,
  position,
  onRemove,
}) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const question: string = Form.useWatch(["questions", name, "question"]) ?? "";
  const answers: { text?: string }[] =
    Form.useWatch(["questions", name, "answers"]) ?? [];
  const correctIndex: number =
    Form.useWatch(["questions", name, "correctIndex"]) ?? 0;
  const needsConfirm = Boolean(
    question.trim() || answers.some((answer) => answer?.text?.trim())
  );

  const removeButton = (
    <Button
      variant="tertiary"
      size="s"
      leftIcon={<TrashIcon size={14} />}
      onClick={needsConfirm ? undefined : onRemove}
    >
      {t(`${KEY}.remove`)}
    </Button>
  );

  return (
    <QuestionBox $token={token}>
      <Flex vertical gap={12}>
        <Flex align="center" justify="space-between">
          <QuestionNumber>
            {t(`${KEY}.questionLabel`, { number: position })}
          </QuestionNumber>
          {needsConfirm ? (
            <Popconfirm
              title={t(`${KEY}.removeConfirm`)}
              onConfirm={onRemove}
              confirmText={t("buttons.delete")}
              cancelText={t("buttons.cancel")}
              danger
            >
              {removeButton}
            </Popconfirm>
          ) : (
            removeButton
          )}
        </Flex>
        {/* Registers the row's session id so it rides along in the form
            values — antd only reports registered paths (see
            `QuickRecallCardItem`, which does the same for its card id). */}
        <Form.Item name={[name, "id"]} noStyle>
          <input type="hidden" />
        </Form.Item>
        <Form.Item name={[name, "question"]} noStyle>
          <Input
            placeholder={t(`${KEY}.question`)}
            aria-label={t(`${KEY}.question`)}
          />
        </Form.Item>
        {/* One radio group per question: the group owns which answer is
            correct, each option's text is its own field beside it. */}
        <Form.Item name={[name, "correctIndex"]} noStyle>
          <RadioGroup aria-label={t(`${KEY}.correctAnswer`)}>
            <Flex vertical gap={8}>
              {ANSWER_INDEXES.map((index) => (
                <AnswerRow
                  key={index}
                  $token={token}
                  $correct={index === correctIndex}
                  align="center"
                  gap={10}
                >
                  <RadioButton
                    value={index}
                    aria-label={t(`${KEY}.markCorrect`, {
                      number: index + 1,
                    })}
                  />
                  <Form.Item name={[name, "answers", index, "text"]} noStyle>
                    <BareInput
                      $token={token}
                      placeholder={t(`${KEY}.answer`, { number: index + 1 })}
                      aria-label={t(`${KEY}.answer`, { number: index + 1 })}
                    />
                  </Form.Item>
                </AnswerRow>
              ))}
            </Flex>
          </RadioGroup>
        </Form.Item>
      </Flex>
    </QuestionBox>
  );
};

export default QuizQuestionItem;

const QuestionBox = styled.div<{ $token: GlobalToken }>`
  padding: 16px;
  border: 1px solid ${({ $token }) => $token.colorBorderSecondary};
  border-radius: ${radius.box}px;
  background: ${({ $token }) => $token.colorBgContainer};
`;

/** The shared Pill, upper-cased into the design's "QUESTION 1" tag. */
const QuestionNumber = styled(Pill)`
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

// The correct option is tinted rather than merely check-marked, so the answer
// key reads at a glance down a long list.
const AnswerRow = styled(Flex)<{ $token: GlobalToken; $correct: boolean }>`
  padding: 8px 12px;
  border: 1px solid
    ${({ $token, $correct }) =>
      $correct ? $token.colorPrimary : $token.colorBorderSecondary};
  border-radius: ${({ $token }) => $token.borderRadiusLG}px;
  background: ${({ $token, $correct }) =>
    $correct ? $token.colorPrimaryBg : $token.colorBgContainer};
  transition: border-color ${({ $token }) => $token.motionDurationMid} ease;
`;

// The row itself draws the border, so the input inside is chrome-free.
const BareInput = styled(Input)<{ $token: GlobalToken }>`
  && {
    flex: 1;
    border: none;
    background: transparent;
    box-shadow: none;
    padding-inline: 0;

    &:focus,
    &:focus-within {
      box-shadow: none;
    }
  }
`;
